import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(value: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(value);
}

/** One currency's share of a total that may span several. */
export type MoneyBreakdown = { currency: string; amount: number };

/**
 * A total that the server may not have been able to express as one number.
 *
 * Amounts are added under a rule: no total spans currencies except through a
 * dated exchange rate, and where there is no rate the server returns null
 * rather than adding unlike units. Rendering that null as `$0` would undo the
 * whole point of returning it — a reader would see zero spend where the truth
 * is spend nobody could convert. So the breakdown is shown instead, which is
 * always a true answer even when a single figure is not available.
 */
export function formatMoneyTotal(
  amount: number | null | undefined,
  currency = 'USD',
  breakdown?: MoneyBreakdown[]
): string {
  if (amount != null) return formatCurrency(amount, currency);
  if (breakdown && breakdown.length > 0) {
    return breakdown.map((b) => formatCurrency(b.amount, b.currency)).join(' + ');
  }
  return '—';
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

export function formatDate(date: Date | string, options?: Intl.DateTimeFormatOptions): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options,
  }).format(d);
}

export function formatRelativeTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - d.getTime()) / 1000);

  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return formatDate(d);
}

export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export function slugify(str: string): string {
  return str
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function truncate(str: string, length: number): string {
  if (str.length <= length) return str;
  return str.slice(0, length) + '...';
}

export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), wait);
  };
}

export function generateId(): string {
  return Math.random().toString(36).substring(2, 15);
}

// =============================================================================
// ADDITIONAL FORMATTING UTILITIES
// =============================================================================

/**
 * Format a date with time
 */
export function formatDateTime(date: Date | string, options?: Intl.DateTimeFormatOptions): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...options,
  }).format(d);
}

/**
 * Format a number as a percentage
 */
export function formatPercent(value: number, decimals = 1): string {
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format a file size in bytes to human-readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Format a decimal number with specified precision
 */
export function formatDecimal(value: number, decimals = 2): string {
  return value.toFixed(decimals);
}

// =============================================================================
// STATUS COLOR UTILITIES
// =============================================================================

export type StatusType = 'success' | 'warning' | 'error' | 'info' | 'neutral';

const STATUS_COLORS: Record<StatusType, string> = {
  success: 'bg-status-success-subtle text-status-success-foreground',
  warning: 'bg-status-warning-subtle text-status-warning-foreground',
  error: 'bg-status-destructive-subtle text-status-destructive-foreground',
  info: 'bg-status-info-subtle text-status-info-foreground',
  neutral: 'bg-muted text-muted-foreground',
};

/**
 * Get Tailwind classes for a status type
 */
export function getStatusColorClass(type: StatusType): string {
  return STATUS_COLORS[type];
}

/**
 * Map common status strings to status types
 */
export function getStatusType(status: string): StatusType {
  const s = status.toUpperCase();

  // Success states
  if (['ACTIVE', 'COMPLETED', 'APPROVED', 'DELIVERED', 'VALID', 'IN_STOCK', 'CLOSED'].includes(s)) {
    return 'success';
  }

  // Warning states
  if (['PENDING', 'SCHEDULED', 'LOW_STOCK', 'ON_ORDER', 'EXPIRING', 'ON_HOLD'].includes(s)) {
    return 'warning';
  }

  // Error states
  if (
    ['CANCELLED', 'REJECTED', 'FAILED', 'EXPIRED', 'OUT_OF_STOCK', 'OVERDUE', 'DELAYED'].includes(s)
  ) {
    return 'error';
  }

  // Info states
  if (['IN_PROGRESS', 'IN_TRANSIT', 'SUBMITTED', 'PROCESSING', 'BOOKED'].includes(s)) {
    return 'info';
  }

  // Default
  return 'neutral';
}

// =============================================================================
// ARRAY AND OBJECT UTILITIES
// =============================================================================

/**
 * Group an array by a key
 */
export function groupBy<T>(array: T[], key: keyof T): Record<string, T[]> {
  return array.reduce(
    (result, item) => {
      const groupKey = String(item[key]);
      (result[groupKey] = result[groupKey] || []).push(item);
      return result;
    },
    {} as Record<string, T[]>
  );
}

/**
 * Remove duplicates from an array
 */
export function unique<T>(array: T[], key?: keyof T): T[] {
  if (key) {
    const seen = new Set();
    return array.filter((item) => {
      const val = item[key];
      if (seen.has(val)) return false;
      seen.add(val);
      return true;
    });
  }
  return [...new Set(array)];
}

/**
 * Sort an array by a key
 */
export function sortBy<T>(array: T[], key: keyof T, direction: 'asc' | 'desc' = 'asc'): T[] {
  return [...array].sort((a, b) => {
    const aVal = a[key];
    const bVal = b[key];
    if (aVal < bVal) return direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return direction === 'asc' ? 1 : -1;
    return 0;
  });
}

// =============================================================================
// NUMBER UTILITIES
// =============================================================================

/**
 * Clamp a number between min and max
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Calculate percentage
 */
export function calculatePercent(value: number, total: number): number {
  if (total === 0) return 0;
  return (value / total) * 100;
}

/**
 * Round to specified decimal places
 */
export function round(value: number, decimals = 2): number {
  return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
}

// =============================================================================
// STRING UTILITIES
// =============================================================================

/**
 * Pluralize a word based on count
 */
export function pluralize(count: number, singular: string, plural?: string): string {
  if (count === 1) return singular;
  return plural || `${singular}s`;
}

/**
 * Format a string as title case
 */
export function toTitleCase(str: string): string {
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Convert snake_case or SCREAMING_SNAKE_CASE to Title Case
 */
export function formatSnakeCase(str: string): string {
  return str
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// =============================================================================
// VALIDATION UTILITIES
// =============================================================================

/**
 * Check if a value is empty (null, undefined, empty string, or empty array)
 */
export function isEmpty(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

/**
 * Check if a string is a valid email
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Check if a string is a valid URL
 */
export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}
